import { formatPrice } from "./Pool_Utils.js";
class Pool_OptionRenderer {
    /**
     * Renders a collection of selectable options as cards.
     * @param {Array} options - List of options to render.
     * @param {string} title - Title for the selection step.
     * @param {Function} onSelectCallback - Callback for handling selection.
     * @param {Function|null} extraInfoCallback - Optional callback for additional option details.
     * @param {string} stepKey - Identifier for the current step.
     * @returns {string} - HTML content for the step.
     */
    static renderOptions(options, title, onSelectCallback, extraInfoCallback = null, stepKey) {
        const containerId = "step-options-container";

        // Delay event binding to ensure DOM is fully updated
        setTimeout(() => this.bindEventListeners(containerId, onSelectCallback, options), 0);

        return `
            <h3 class="text-center fw-bold fs-3">${title}</h3>
            <div id="${containerId}" class="row g-3">
                ${options.map(opt => this.renderOptionCard(opt, extraInfoCallback, stepKey)).join("")}
            </div>
        `;
    }

    /**
     * Renders a single option card.
     * @param {Object} option - The option object.
     * @param {Function|null} extraInfoCallback - Callback for additional details.
     * @param {string} stepKey - The step identifier.
     * @returns {string} - HTML for an option card.
     */
    static renderOptionCard(option, extraInfoCallback, stepKey = "") {
        const isOutOfStock = option.stock === 0;
        const isDefault = option.isDefault || false;
        const columnClass = stepKey === "equipment" ? "col-md-4" : "col-md-3";
        const imagesHTML = Array.isArray(option.image)
        ? option.image.map(img => `<img src="${img}" alt="${option.name}" class="img-fluid mx-1 rounded w-auto object-fit-contain" style="max-width: 80px;">`).join("")
        : `<img src="${option.image}" alt="${option.name}" class="card-img-top img-fluid">`;    

        let displayPrice = "";
        if (stepKey === "liner") {
            displayPrice = isDefault 
                ? `<p class="card-text fw-bold fs-6">&nbsp;</p>`
                : `<p class="card-text fw-bold fs-6 text-success">${option.priceDifference}</p>`;
        }
        else if (["pool-style", "pool-shape", "pool-size"].includes(stepKey)) {
            displayPrice = option.startingPrice ? `<p class="card-text fw-bold fs-6">Starting at ${formatPrice(option.startingPrice)}</p>` : "";
        } else {
            displayPrice = option.price ? `<p class="card-text fw-bold fs-6">${formatPrice(option.price)}</p>` : "";
        }

        return `
            <div class="${columnClass}">
                <div class="card h-100 text-center shadow-sm ${isOutOfStock ? "disabled bg-dark-subtle" : ""}">
                    <div class="card-body">
                        <h5 class="card-title fw-bold fs-4">${option.name}</h5>
                        ${imagesHTML}
                        ${displayPrice}
                        ${extraInfoCallback ? extraInfoCallback(option) : ""}
                        ${isOutOfStock ? '<p class="text-danger fw-bold fs-5">Out of Stock</p>'
                : `<button class="btn ${isDefault ? "btn-success" : "btn-primary"} btn-lg select-button fw-bold" data-item-id="${option.id}">
                        ${isDefault ? "Default" : "Select"}
                        </button>`}
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Formats price values.
     * @param {number|null} price - Price to format.
     * @param {string} prefix - Optional prefix (e.g., "Starting At: ").
     * @returns {string} - Formatted price string.
     */
    static formatPrice(price, prefix = "") {
        return price && !isNaN(price) ? `${prefix}$${parseFloat(price).toFixed(2)}` : "";
    }

    /**
     * Binds click event listeners to selection buttons.
     * @param {string} containerId - ID of the container holding options.
     * @param {Function} onSelectCallback - Callback for handling selection.
     * @param {Array} options - List of selectable options.
     */
    static bindEventListeners(containerId, onSelectCallback, options) {
        document.querySelectorAll(`#${containerId} .select-button`).forEach(button => {
            if (button.dataset.listenerAttached) return;
            button.dataset.listenerAttached = "true";

            button.addEventListener("click", () => {
                const selectedOption = options.find(opt => opt.id === button.getAttribute("data-item-id"));
                onSelectCallback(selectedOption);
            });
        });
    }
}

export { Pool_OptionRenderer };
